import React, { useState, useCallback } from 'react';
import { servicesService } from '../../services/services.service';
import type { ServiceEventResponse, ServiceEvent } from '../../services/services.service';
import ServiceEventsList from './ServiceEventsList/ServiceEventsList';

// ─── mapping helpers (duplicated from pages, centralised here) ────────────────

function mapLevelToType(level?: string): ServiceEvent['type'] {
  switch ((level || '').toUpperCase()) {
    case 'SUCCESS': return 'success';
    case 'ERROR':   return 'error';
    case 'WARNING': return 'warning';
    default:        return 'info';
  }
}

function mapStackStatusToType(s?: string): ServiceEvent['type'] {
  const st = (s || '').toUpperCase();
  if (st.includes('COMPLETE') && !st.includes('FAILED')) return 'success';
  if (st.includes('FAILED') || st.includes('ERROR') || st.includes('ROLLBACK')) return 'error';
  return 'info';
}

function mapRawEvents(raw: ServiceEventResponse[]): ServiceEvent[] {
  return raw.map((e, i) => {
    const msgLower = (e.message || '').toLowerCase();
    return {
      id: e.PK || e.SK || `event-${i}`,
      timestamp: new Date(e.createdAt),
      message: e.message,
      type: e.level ? mapLevelToType(e.level) : mapStackStatusToType(e.stackStatus),
      isKubiyEvent: e.source?.toLowerCase() === 'kubiy',
      isDeletionEvent: msgLower.includes('deleted') || msgLower.includes('eliminado'),
    };
  });
}

// ─── component ────────────────────────────────────────────────────────────────

interface LazyServiceEventsListProps {
  serviceId: string;
  title?: string;
  emptyMessage?: string;
  eventsPerPage?: number;
}

type Phase = 'idle' | 'loading' | 'loaded' | 'error';

const LazyServiceEventsList: React.FC<LazyServiceEventsListProps> = ({
  serviceId,
  title = 'Eventos',
  emptyMessage = 'No hay eventos disponibles',
  eventsPerPage = 10,
}) => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [events, setEvents] = useState<ServiceEvent[]>([]);
  const [errorMsg, setErrorMsg] = useState('');

  const load = useCallback(async () => {
    if (!serviceId) return;
    setPhase('loading');
    try {
      const raw = await servicesService.getEvents(serviceId);
      setEvents(mapRawEvents(raw));
      setPhase('loaded');
    } catch (e: any) {
      setErrorMsg(e?.message || 'Error al cargar eventos');
      setPhase('error');
    }
  }, [serviceId]);

  if (phase === 'idle') {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <svg className="h-10 w-10 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        <p className="text-sm text-gray-500 dark:text-gray-400">Los eventos se cargan bajo demanda</p>
        <button
          onClick={load}
          className="px-4 py-2 text-sm font-medium bg-black dark:bg-white text-white dark:text-black rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
        >
          Ver últimos eventos
        </button>
      </div>
    );
  }

  if (phase === 'loading') {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 dark:border-white" />
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <p className="text-sm text-red-600 dark:text-red-400">{errorMsg}</p>
        <button
          onClick={load}
          className="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <ServiceEventsList
      events={events}
      title={title}
      emptyMessage={emptyMessage}
      refreshLabel="Actualizar"
      onRefresh={load}
      eventsPerPage={eventsPerPage}
    />
  );
};

export default LazyServiceEventsList;
